import { envConfig } from '#constants/envConfig';

const formats = new Set(['webp', 'png', 'jpg', 'jpeg', 'avif', 'gif', 'svg']);

function hasStoredFormat(path: string): boolean {
  if (path.startsWith('/logo/')) {
    return /\/[^/]+\.(webp|png|jpe?g|avif|gif|svg)$/i.test(path);
  }
  if (!path.startsWith('/imgproxy/')) return false;

  const segments = path.slice('/imgproxy/'.length).split('/');
  if (!segments[0] || segments.length < 3 || !segments.at(-1)) return false;
  // Stop at a plain source: its extensions and path segments are not output options.
  for (const segment of segments.slice(1, -1)) {
    if (segment === 'plain') return false;
    const option = /^(?:f|format|ext):(.+)$/.exec(segment);
    if (option) return formats.has(option[1].toLowerCase());
  }
  const extension = /\.([^.]+)$/.exec(segments.at(-1)!);
  return Boolean(extension && formats.has(extension[1].toLowerCase()));
}

/**
 * WKWebView cannot intercept HTTPS images. Route eligible display URLs to its native
 * scheme handler after resizing/signing; API data and URLs passed to prewarm stay HTTPS.
 * Android intercepts HTTPS itself. A web build (including Safari on iOS) keeps HTTPS.
 */
export function nativeImageUrl<T extends string | null | undefined>(url: T): T {
  if (!url || !envConfig.IS_NATIVE || typeof window === 'undefined') return url;
  const bridge = (window as Window & { Capacitor?: { getPlatform?: () => string } }).Capacitor;
  if (bridge?.getPlatform?.() !== 'ios') return url;

  // Check the original authority as well: URL normalises away an explicit :443 port.
  if (!/^https:\/\/(?:media\.teamplay\.gg|media\.dev\.teamplay\.gg)\//i.test(url)) return url;
  try {
    const parsed = new URL(url);
    if (parsed.username || parsed.password || parsed.port || !hasStoredFormat(parsed.pathname)) {
      return url;
    }
    // Preserve the signed path/query byte-for-byte and make the mapping idempotent.
    return url.replace(/^https:/i, 'teamplay-image:') as T;
  } catch {
    return url;
  }
}
